import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Periods } from "@hometoq/backend/enums";
import { toast } from "sonner";
import { addDays, format, startOfDay } from "date-fns";
import { usePostHogEvents } from "./usePosthogEvents";
import { useMutation } from "@tanstack/react-query";
import { customRequestMutations } from "@/domain/custom-request/custom-request.query";

const customRequestSchema = z.object({
  chefType: z.enum(["DOMICILE", "DEMEURE"], {
    required_error: "Veuillez sélectionner un type de chef",
  }),
  date: z
    .date()
    .refine((val) => val >= startOfDay(addDays(new Date(), 2)), {
      message: "Veuillez sélectionner une date",
    }),
  dateTo: z.date().optional(),
  timeSlot: z.enum([Periods.MORNING, Periods.AFTERNOON], {
    required_error: "Veuillez sélectionner une période",
  }),
  city: z.string().min(1, "Veuillez renseigner votre ville"),
  guestCount: z.number().min(1, "Au moins 1 invité est requis"),
  budget: z
    .string()
    .min(1, "Veuillez renseigner votre budget")
    .refine((value) => {
      const budget = Number(value);
      return Number.isFinite(budget) && budget > 0;
    }, "Le budget doit etre positif"),
  foodPreferences: z.string().optional(),
  dietaryRestrictions: z.string().optional(),
  message: z.string().optional(),
  fullName: z.string().min(1, "Le nom complet est requis"),
  email: z.string().email("Email invalide"),
  phone: z.string().min(1, "Le numéro de téléphone est requis"),
});

export type CustomRequestFormData = z.infer<typeof customRequestSchema>;

export const useCustomRequestForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = 8;
  const { trackCustomRequest } = usePostHogEvents();
  const createCustomRequestMutation = useMutation(customRequestMutations.create());

  const form = useForm<CustomRequestFormData>({
    resolver: zodResolver(customRequestSchema),
    mode: "onChange",
    defaultValues: {
      chefType: "DOMICILE",
      date: new Date(),
      dateTo: undefined,
      timeSlot: Periods.AFTERNOON,
      city: "",
      guestCount: 1,
      budget: "",
      foodPreferences: "",
      dietaryRestrictions: "",
      message: "",
      fullName: "",
      email: "",
      phone: "",
    },
  });

  const nextStep = async () => {
    const currentStepFields = getCurrentStepFields();
    const isValid = await form.trigger(currentStepFields);

    if (!isValid) return;

    // For DEMEURE, require a date range
    if (currentStep === 1 && form.getValues("chefType") === "DEMEURE") {
      if (!form.getValues("dateTo")) {
        form.setError("dateTo" as never, {
          message: "Veuillez sélectionner une date de fin",
        });
        return;
      }
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const getCurrentStepFields = (): (keyof CustomRequestFormData)[] => {
    switch (currentStep) {
      case 0:
        return ["chefType"];
      case 1:
        return form.getValues("chefType") === "DEMEURE"
          ? ["date"]
          : ["date", "timeSlot"];
      case 2:
        return ["city"];
      case 3:
        return ["guestCount"];
      case 4:
        return ["budget"];
      case 5:
        return ["foodPreferences", "dietaryRestrictions"];
      case 6:
        return ["message"];
      case 7:
        return ["fullName", "email", "phone"];
      default:
        return [];
    }
  };

  const getProgress = () => {
    return ((currentStep + 1) / totalSteps) * 100;
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const onSubmit = async (data: CustomRequestFormData) => {
    trackCustomRequest({
      chefType: data.chefType,
      date: format(data.date, "yyyy-MM-dd"),
      city: data.city,
      guestCount: data.guestCount,
      budget: parseInt(data.budget),
      email: data.email,
      fullName: data.fullName,
    });

    const result = await createCustomRequestMutation
      .mutateAsync({
        type: data.chefType,
        date: format(data.date, "yyyy-MM-dd"),
        period: data.timeSlot,
        city: data.city,
        guests: data.guestCount,
        budget: parseInt(data.budget),
        preferences: data.foodPreferences,
        restrictions: data.dietaryRestrictions,
        message: data.message,
        name: data.fullName,
        email: data.email,
        phone: data.phone,
      })
      .catch(() => null);

    if (!result) return;

    toast.success("Votre demande a été envoyée avec succès !");
    form.reset();
    setCurrentStep(0);
  };

  return {
    form,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    getProgress,
    isFirstStep,
    isLastStep,
    onSubmit,
    getCurrentStepFields,
  };
};
